import { ChevronDown, ChevronRight, Folder, FolderOpen } from "lucide-react";

/**
 * Flatten a tree (rows with parent_id) into a depth-tagged list, respecting
 * `expanded`. Iterative — no self-referencing JSX (avoids a known babel-traverse
 * infinite loop bug on recursive components inside a single module).
 */
export function flattenCategoryTree(rows, expanded) {
  const byId = Object.fromEntries(rows.map((r) => [r.id, { ...r, children: [] }]));
  const roots = [];
  for (const r of rows) {
    if (r.parent_id && byId[r.parent_id]) byId[r.parent_id].children.push(byId[r.id]);
    else roots.push(byId[r.id]);
  }
  const out = [];
  const stack = roots.map((n) => ({ node: n, depth: 0 }));
  while (stack.length) {
    const { node, depth } = stack.shift();
    const hasChildren = node.children && node.children.length > 0;
    out.push({ ...node, depth, hasChildren });
    if (hasChildren && expanded.has(node.id)) {
      // Insert children at the front so DFS order is preserved.
      const childItems = node.children.map((c) => ({ node: c, depth: depth + 1 }));
      stack.unshift(...childItems);
    }
  }
  return out;
}

export default function KbCategoryTree({ rows, expanded, setExpanded, activeId, onPick }) {
  const flat = flattenCategoryTree(rows, expanded);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      {flat.map((node) => {
        const isOpen = expanded.has(node.id);
        const active = activeId === node.id;
        return (
          <div
            key={node.id}
            className={`flex items-center gap-1 ${active ? "bg-indigo-50 text-indigo-700 rounded" : "text-slate-700"}`}
          >
            <button
              type="button"
              onClick={() => toggle(node.id)}
              className="w-4 h-4 inline-flex items-center justify-center text-slate-400 hover:text-slate-700"
            >
              {node.hasChildren ? (isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />) : null}
            </button>
            <button
              type="button"
              onClick={() => onPick(node.id)}
              style={{ paddingLeft: `${4 + node.depth * 8}px` }}
              className={`flex-1 text-left px-1 py-1 rounded text-xs flex items-center gap-1.5 ${active ? "font-medium" : "hover:bg-slate-100"}`}
              data-testid={`kb-category-${node.id}`}
            >
              {isOpen ? <FolderOpen className="w-3.5 h-3.5 text-slate-400" /> : <Folder className="w-3.5 h-3.5 text-slate-400" />}
              <span className="truncate flex-1">{node.name}</span>
              <span className="text-[10px] text-slate-400">{node.article_count || 0}</span>
            </button>
          </div>
        );
      })}
    </>
  );
}
